import { useEffect, useState } from "react";
import { Button, Card, Form, Spin, message } from "antd";
import { CheckOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { db } from "../../../firebase/firebase";
import { collection, query, where, getDocs, updateDoc, serverTimestamp } from "firebase/firestore";
import CvUpload from "./components/updateCvUpload";
import { storage as localStorageUtil } from "utils/storage";
import { CvItem } from "views/Profile/components/viewCvItemPreview";

// Fetch user info from Firestore
const fetchUserInfo = async (uid: string) => {
  try {
    const userInfoQuery = query(
      collection(db, "user_infos"),
      where("uid", "==", uid),
    );
    const querySnapshot = await getDocs(userInfoQuery);
    if (querySnapshot.empty) throw new Error("User profile not found.");
    const userDoc = querySnapshot.docs[0];
    return { ...userDoc.data(), docRef: userDoc.ref };
  } catch (error: any) {
    throw new Error("Error fetching user profile: " + error.message);
  }
};

const EnumeratorProfileForm = () => {
  const [loading, setLoading] = useState(true);
  const [userinfo, setUserInfo] = useState(null);
  const navigate = useNavigate();
  const [form] = Form.useForm();

  useEffect(() => {
    const fetchAndSetUserData = async () => {
      try {
        const storedUser = localStorageUtil.getUserFromStorage();
        if (!storedUser?.uid) {
          message.error("User not authenticated.");
          setLoading(false);
          return;
        }

        const userInfo = await fetchUserInfo(storedUser.uid);
        setUserInfo(userInfo);

        // Set form values only if userInfo exists
        if (userInfo) {
          form.setFieldsValue({ cv: userInfo.cv || null });
        }
      } catch (error) {
        message.info("Failed to load user profile.");
        return;
      } finally {
        setLoading(false);
      }
    };

    fetchAndSetUserData();
  }, [form]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      if (!userinfo?.docRef) {
        message.error("User profile not found.");
        return;
      }

      await updateDoc(userinfo.docRef, {
        cv: values.cv,
        updatedAt: serverTimestamp(),
      });
      if (values.cv.file) {
        message.success("CV saved successfully!");
        navigate("/enumerator/home");
      } else {
        message.success("CV deleted successfully!");
        navigate("/enumerator/home");
      }
    } catch (error: any) {
      message.error("Error updating profile: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-background min-h-screen px-5 md:px-0">
      {loading && (
        <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-60 z-10">
          <Spin className="w-full max-w-4xl" />
        </div>
      )}

      <div className="max-w-4xl mx-auto py-12">
        {/* Reminder notice */}
        <Card size="small" className="mb-6 bg-yellow-50 border border-yellow-200">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
            <span className="text-sm text-yellow-800 font-bold">
              Don’t forget to keep your profile up to date!!!
            </span>
            <Button

              type="link"
              onClick={() => navigate("/enumerator/profile/update")}
              className="pl-0 md:pl-4 text-blue-800"
            >
              Update Profile
            </Button>
          </div>
        </Card>

        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Card className="px-8 py-2 mt-8">
            {userinfo && (
              <>
                <CvUpload setLoadingCv={setLoading} form={form} user={{ uid: userinfo.uid, cv: userinfo.cv || "" }} />

                {userinfo.cv && (
                  <div style={{ marginTop: "20px" }}>
                    <CvItem item={userinfo.cv} />
                  </div>
                )}
              </>
            )}

            <div className="flex flex-col md:flex-row gap-y-3 justify-between pt-12">
              <Button
                onClick={() => navigate("/enumerator/profile")}
                disabled={loading}
              >
                <ClockCircleOutlined /> Cancel
              </Button>
              <Button htmlType="submit" type="primary" loading={loading}>
                <CheckOutlined /> Submit CV
              </Button>
            </div>
          </Card>
        </Form>
      </div>
    </div>
  );
};

export default EnumeratorProfileForm;
